import { Box, Stack } from '@mantine/core';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { useCallback, useEffect, useRef } from 'react';
import Message from './Message';
import ThinkingMessage from './ThinkingMessage';

interface MessagesProps {
    chatId: string;
    onScrollStatusChange: (show: boolean) => void;
    setScrollToBottom: (fn: () => void) => void;
}

const Messages: React.FC<MessagesProps> = ({
    chatId,
    onScrollStatusChange,
    setScrollToBottom,
}) => {
    const { aiChatMessages, isStreaming } = useAiAnalystContext(
        (context) => context.state,
    );
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    //@ts-ignore
    const handleScrollRef = useRef<() => void>();

    const scrollToBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
            return;
        }

        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        }
    }, []);

    useEffect(() => {
        setScrollToBottom(scrollToBottom);
    }, [setScrollToBottom, scrollToBottom]);

    useEffect(() => {
        const raf = requestAnimationFrame(scrollToBottom);
        return () => cancelAnimationFrame(raf);
    }, [aiChatMessages, isStreaming, scrollToBottom]);

    useEffect(() => {
        const handleScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;
            const distanceFromBottom =
                container.scrollHeight -
                (container.scrollTop + container.clientHeight);
            const isNearBottom = distanceFromBottom < 120;
            const isScrollable =
                container.scrollHeight - container.clientHeight > 80;
            onScrollStatusChange(isScrollable && !isNearBottom);
        };

        handleScrollRef.current = handleScroll;
        const container = scrollContainerRef.current;
        if (!container) return;
        container.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => container.removeEventListener('scroll', handleScroll);
    }, [onScrollStatusChange]);

    useEffect(() => {
        handleScrollRef.current?.();
    }, [aiChatMessages, isStreaming]);

    return (
        <Box
            ref={scrollContainerRef}
            className="scrollbar-hide min-h-0 w-full flex-1 overflow-y-auto"
        >
            <Stack className="w-full flex-1 gap-4 p-4 pb-24">
                {aiChatMessages.map((message, idx) => {
                    const isLast = idx === aiChatMessages.length - 1;
                    const prev = aiChatMessages[idx - 1];
                    const showAnchor =
                        message.author !== 'user' &&
                        (!prev || prev.author === 'user');
                    return (
                        <Message
                            key={message.messageId}
                            message={message}
                            isLast={isLast}
                            isStreaming={isStreaming}
                            showAnchor={showAnchor}
                        />
                    );
                })}
                {isStreaming && (
                    <Box className="w-full text-left break-words">
                        <ThinkingMessage label={'Processing'} />
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Stack>
        </Box>
    );
};

export default Messages;
